'use client'

import { useLocationMetadata, useModals, useOrderProperties } from '@/lib/hooks'
import React from 'react'
import { toast } from 'sonner'

import { Button, DateTimePicker, Label, DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from '@/components/ui'
import { CheckCircle, Loader2, ChevronDown } from 'lucide-react'

interface SetOrderPropertiesProps extends React.ComponentPropsWithoutRef<'form'> {
  onClose: () => void
  cartId: string
  onSuccess?: () => void | Promise<void>
}

export const SetOrderProperties = ({ onClose, cartId, onSuccess }: SetOrderPropertiesProps) => {
  const [isPending, startTransition] = React.useTransition()
  const { closeModal } = useModals()
  const { updateOrderProperties } = useOrderProperties()
  const {
    order_sources,
    delivery_methods,
    future_order_types,
  } = useLocationMetadata()

  const [orderSource, setOrderSource] = React.useState<string>('')
  const [deliveryMethod, setDeliveryMethod] = React.useState<string>('')

  const handleSubmit = (data: FormData) => {
    const pickup_time = data.get('pickup_time') as string
    const fulfillment_time = data.get('fulfillment_time') as string
    const order_source = orderSource
    const delivery_method = deliveryMethod

    // Validate all required fields - early exit pattern
    if (!pickup_time) {
      toast.error('Pickup time is required')
      return
    }

    if (!fulfillment_time) {
      toast.error('Fulfillment time is required')
      return
    }

    if (!order_source) {
      toast.error('Order source is required')
      return
    }

    if (!delivery_method) {
      toast.error('Delivery method is required')
      return
    }

    console.log({
      pickup_time,
      fulfillment_time,
      order_source,
      delivery_method,
    })

    startTransition(async () => {
      try {
        // Update order properties first
        await updateOrderProperties({
          cartId,
          pickup_time,
          fulfillment_time,
          order_source,
          delivery_method,
        })

        // Call onSuccess callback if provided (e.g., to end sale/create order)
        if (onSuccess) {
          try {
            await onSuccess()
          } catch (orderError: any) {
            // Handle order creation errors separately
            const errorMessage = typeof orderError === 'string'
              ? orderError
              : orderError?.message || 'Failed to complete order'

            toast.error(errorMessage)
            console.error('Order creation failed:', orderError)
            // Don't close modal on order creation failure
            return
          }
        }

        // Only close modal if everything succeeded
        onClose?.()
      } catch (error: any) {
        // Handle order properties update errors
        const errorMessage = typeof error === 'string'
          ? error
          : error?.message || 'Failed to update order properties'

        toast.error(errorMessage)
        console.error('Failed to update order properties:', error)
        // Don't close modal on error
      }
    })
  }

  return (
    <form className="flex w-full flex-col gap-3 pt-2" action={handleSubmit}>
      <div className="flex flex-col gap-1">
        <Label className="md:text-sm font-medium" htmlFor="pickup_time">Pickup Time</Label>
        <DateTimePicker
          id="pickup_time"
          name="pickup_time"
          disabled={isPending}
          placeholder='Select Pickup Time'
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="md:text-sm font-medium" htmlFor="fulfillment_time">Fulfillment Time</Label>
        <DateTimePicker
          id="fulfillment_time"
          name="fulfillment_time"
          disabled={isPending}
          placeholder='Select Fulfillment Time'
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="md:text-sm font-medium" htmlFor="order_source">Order Source</Label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              {orderSource ? order_sources?.find(s => s.value === orderSource)?.label : "Select Order Source"}
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[--radix-dropdown-menu-trigger-width] max-h-[300px] overflow-y-auto">
            <DropdownMenuRadioGroup value={orderSource} onValueChange={setOrderSource}>
              {order_sources?.map((source) => (
                <DropdownMenuRadioItem key={source.value} value={source.value}>
                  {source.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex flex-col gap-1">
        <Label className="md:text-sm font-medium" htmlFor="delivery_method">Delivery Method</Label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              {deliveryMethod ? delivery_methods?.find(m => m.value === deliveryMethod)?.label : "Select Delivery Method"}
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[--radix-dropdown-menu-trigger-width] max-h-[300px] overflow-y-auto">
            <DropdownMenuRadioGroup value={deliveryMethod} onValueChange={setDeliveryMethod}>
              {delivery_methods?.map((method) => (
                <DropdownMenuRadioItem key={method.value} value={method.value}>
                  {method.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex flex-col-reverse items-center gap-2 sm:flex-row pt-6">
        <Button
          variant="secondary"
          size="lg"
          className="w-full"
          onClick={() => closeModal('SELECT_NEW_ORDER_OPTIONS')}
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="lg"
          className="w-full"
          disabled={isPending}
          type="submit"
        >
          {isPending && <Loader2 className='animate-spin size-5' />}
          {!isPending && (
            <>
              <CheckCircle className="h-4 w-4" />
              Set Properties
            </>
          )}
        </Button>
      </div>
    </form >
  )
}
